import type { ReactNode } from 'react';
import {
  Center,
  Flex,
  Icon,
  Tooltip,
  type TooltipProps,
} from '@chakra-ui/react';
import { Tag } from '@kula-ai/components';
import { KFormLabel } from '@/ui-library/components';
import { MarkdownSubtext } from '@/utils/MarkdownSubtext';
import { InfoCircle, Lock01 } from '@kula-ai/icons-core';
import { colors } from '@/theme/foundations/colors';

type FormCustomLabelItem = {
  label?: string;
  name?: string;
  subLabel?: string;
  labelStyles?: React.CSSProperties;
  subLabelStyles?: React.CSSProperties;
  type?: string;
  labelInfoText?: string;
  labelInfoIconColor?: string;
  labelInfoTextPlacement?: TooltipProps['placement'];
  showLabel?: boolean;
  // "Prevent edits": the value is settled and the filler cannot change it.
  // Says so beside the label, since a greyed input alone reads as broken.
  locked?: boolean;
  // Anything else that belongs beside the label — a calculated field hangs
  // its Formula tag here rather than replacing the control with its own.
  labelBadge?: ReactNode;
};

// Matches the Formula tag a calculated field carries, minus the word: both
// mean "this value is not yours to type".
const LockedBadge = () => (
  <Tag
    size="sm"
    label=""
    leftElement={<Lock01 size={12} color={colors['primary-600']} />}
    style={{ background: colors['primary-50'], color: colors['primary-600'] }}
    data-test-id="locked-field-badge"
  />
);

export const FormCustomLabel = ({
  item = {},
}: {
  item?: FormCustomLabelItem;
}) => {
  const {
    label,
    name,
    subLabel,
    labelStyles,
    subLabelStyles,
    type,
    labelInfoText,
    labelInfoIconColor,
    labelInfoTextPlacement,
    showLabel = true,
    locked = false,
    labelBadge,
  } = item;

  if (['checkbox', 'switch'].includes(type || '')) return null;

  return (
    <>
      {label && showLabel && (
        <Flex alignItems="center">
          <KFormLabel
            htmlFor={String(name)}
            overflow="hidden"
            style={{ ...labelStyles }}
          >
            {label}
          </KFormLabel>

          {locked && !labelBadge && (
            <Center pr="8px" pb="4px">
              <LockedBadge />
            </Center>
          )}

          {/* The Formula tag already carries the padlock, so the two never
              stack up beside one label. */}
          {labelBadge && (
            <Center pr="8px" pb="4px">
              {labelBadge}
            </Center>
          )}

          {labelInfoText && (
            <Tooltip
              variant="default"
              label={labelInfoText}
              placement={labelInfoTextPlacement}
            >
              <Center pr="12px" pb="4px">
                <Icon
                  as={InfoCircle as React.ElementType}
                  {...(labelInfoIconColor && { color: labelInfoIconColor })}
                />
              </Center>
            </Tooltip>
          )}
        </Flex>
      )}

      {subLabel && (
        <KFormLabel
          htmlFor={name}
          style={{ ...subLabelStyles }}
          variant="subLabel"
          requiredIndicator={null}
        >
          <MarkdownSubtext
            markdown={subLabel}
            bodyColor="currentColor"
            bg="transparent"
            p={0}
            m={0}
          />
        </KFormLabel>
      )}
    </>
  );
};
